'use client';

import { ContactLensesProps } from '@/types/Product';
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  Icon,
  Portal,
  Select,
  Text,
  createListCollection,
} from '@chakra-ui/react';

import { useEffect, useState } from 'react';
import { FaEye } from 'react-icons/fa6';
import { MdOutlineArrowBackIos } from 'react-icons/md';
import SphericalPowerSelect from '../ContactLensesSphericalSelect';
import { formatCylindricalPower } from '@/utils/formatCylindricalPower';

type EyeOption = {
  base_curve?: string[];
  diameter?: string[];
  spherical_power?: string[];
  cylindrical_power?: string[];
  axle?: string[];
  addition?: string[];
};

type LensColor = { value: string; hex: string };

const EYE_OPTIONS = [
  { value: 'left' as const, label: 'OE', description: 'Olho Esquerdo', count: 1 },
  { value: 'right' as const, label: 'OD', description: 'Olho Direito', count: 1 },
  { value: 'both' as const, label: 'OE + OD', description: 'Ambos', count: 2 },
];

const PARAM_LABELS: { key: keyof EyeOption; label: string }[] = [
  { key: 'base_curve', label: 'Curva Base' },
  { key: 'diameter', label: 'Diâmetro' },
  { key: 'spherical_power', label: 'Potência Esférica' },
  { key: 'cylindrical_power', label: 'Potência Cilíndrica' },
  { key: 'axle', label: 'Eixo' },
  { key: 'addition', label: 'Adição' },
];

const ContactLensesOptions = ({
  contact,
  stageDefault = 1,
  onFormComplete,
  onReset,
}: {
  contact: ContactLensesProps | null;
  stageDefault: number;
  onFormComplete: (data: string) => void;
  onReset?: () => void;
}) => {
  const hasColors = !!contact && contact.color.length > 0;

  // Quando a lente tem cores configuradas, o primeiro passo (0) é escolher
  // a cor — a mesma para os dois olhos, por isso é pedida uma só vez, antes
  // da escolha do(s) olho(s).
  const [stage, setStage] = useState(() => (hasColors ? 0 : stageDefault));
  const [selectedColor, setSelectedColor] = useState<LensColor | null>(null);
  const [selectedEye, setSelectedEye] = useState<
    'left' | 'right' | 'both' | null
  >(null);
  const [leftEye, setLeftEye] = useState<EyeOption | null>(null);
  const [rightEye, setRightEye] = useState<EyeOption | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const areAllFieldsFilled = (
    eye: EyeOption | null,
    contact: ContactLensesProps,
  ) => {
    if (!eye) return false;
    if (contact.base_curve.length > 0 && !eye.base_curve?.[0]) return false;
    if (contact.diameter.length > 0 && !eye.diameter?.[0]) return false;
    if (contact.spherical_power.length > 0 && !eye.spherical_power?.[0])
      return false;
    if (contact.cylindrical_power?.length > 0 && !eye.cylindrical_power?.[0])
      return false;
    if (contact.axle?.length > 0 && !eye.axle?.[0]) return false;
    if (contact.addition?.length > 0 && !eye.addition?.[0]) return false;
    return true;
  };

  useEffect(() => {
    if (stage === 2 && contact) {
      if (contact.base_curve.length === 1) {
        if (selectedEye === 'left' || selectedEye === 'both')
          setLeftEye((pv) => ({ ...pv, base_curve: [contact.base_curve[0].value] }));
        if (selectedEye === 'right' || selectedEye === 'both')
          setRightEye((pv) => ({ ...pv, base_curve: [contact.base_curve[0].value] }));
      }
      if (contact.diameter.length === 1) {
        if (selectedEye === 'left' || selectedEye === 'both')
          setLeftEye((pv) => ({ ...pv, diameter: [contact.diameter[0].value] }));
        if (selectedEye === 'right' || selectedEye === 'both')
          setRightEye((pv) => ({ ...pv, diameter: [contact.diameter[0].value] }));
      }
    }
  }, [contact, selectedEye, stage]);

  useEffect(() => {
    if (stage === 2 && selectedEye && contact) {
      let ready = false;
      if (selectedEye === 'left') {
        ready = areAllFieldsFilled(leftEye, contact);
      } else if (selectedEye === 'right') {
        ready = areAllFieldsFilled(rightEye, contact);
      } else if (selectedEye === 'both') {
        ready =
          areAllFieldsFilled(leftEye, contact) &&
          areAllFieldsFilled(rightEye, contact);
      }
      setIsReady(ready);
      if (!ready) setShowConfirm(false);
    } else {
      setIsReady(false);
    }
  }, [leftEye, rightEye, stage, selectedEye, contact]);

  if (!contact) return null;

  const getVal = (
    v: string | string[] | undefined,
    key?: keyof EyeOption,
  ): string => {
    if (!v) return '—';
    const raw = Array.isArray(v) ? (v[0] ?? '—') : String(v) || '—';
    return key === 'cylindrical_power' && raw !== '—'
      ? formatCylindricalPower(raw)
      : raw;
  };

  const renderSelect = (
    eye: 'left' | 'right',
    data: { value: string }[],
    type:
      | 'base_curve'
      | 'diameter'
      | 'spherical_power'
      | 'cylindrical_power'
      | 'axle'
      | 'addition',
  ) => {
    // Do valor mais pequeno para o maior — trata valores negativos
    // corretamente (ex: cilíndrica) e cai para ordem alfabética quando o
    // valor não é numérico (ex: adições em texto como "LOW"/"HIGH").
    const sortedData = [...data].sort((a, b) => {
      const aNum = parseFloat(a.value.replace(',', '.'));
      const bNum = parseFloat(b.value.replace(',', '.'));
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
      return a.value.localeCompare(b.value);
    });

    const collection = createListCollection({
      items: sortedData.map((opt) => ({
        label:
          type === 'cylindrical_power'
            ? formatCylindricalPower(opt.value)
            : opt.value,
        value: opt.value,
      })),
    });

    if (data.length === 1) {
      return (
        <Select.Root collection={collection} value={[data[0].value]} disabled size="sm" w="100%">
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
        </Select.Root>
      );
    }

    return (
      <Select.Root
        collection={collection}
        value={(() => {
          const v = eye === 'left' ? leftEye?.[type] : rightEye?.[type];
          if (typeof v === 'string') return [v];
          if (Array.isArray(v)) return v;
          return undefined;
        })()}
        onValueChange={(value) => {
          const selectedValue =
            Array.isArray(value) && value.length > 0
              ? (value[0]?.value ?? value[0])
              : typeof value === 'object' &&
                  value !== null &&
                  'value' in value
                ? value.value
                : value;
          if (eye === 'left') {
            setLeftEye((prev) => ({ ...prev, [type]: selectedValue }));
          } else {
            setRightEye((prev) => ({ ...prev, [type]: selectedValue }));
          }
        }}
        size="sm"
        w="100%"
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <Select.ValueText placeholder="—" />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}>
              {collection.items.map((opt, index) => (
                <Select.Item item={opt} key={`${opt.value}-${index}`}>
                  {opt.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    );
  };

  /* ── Stage 0: Color selection (only when the lens has colors) ── */
  if (stage === 0) {
    return (
      <Flex flexDir="column" gap="14px" w="100%">
        <Flex align="center" gap="8px">
          <Flex
            w="28px"
            h="28px"
            bg="rgba(0,176,202,0.1)"
            borderRadius="8px"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Icon w="13px" h="13px" color="#00B0CA">
              <FaEye />
            </Icon>
          </Flex>
          <Text fontSize="14px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
            Escolha a cor
          </Text>
        </Flex>
        <Grid templateColumns={{ base: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }} gap="10px">
          {contact.color.map((color) => {
            const isSelected = selectedColor?.value === color.value;
            return (
              <Flex
                key={color.value}
                as="button"
                flexDir="column"
                align="center"
                justify="center"
                gap="8px"
                py="14px"
                px="8px"
                borderRadius="16px"
                border="2px solid"
                borderColor={
                  isSelected
                    ? '#00B0CA'
                    : { base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }
                }
                bg={
                  isSelected
                    ? { base: 'rgba(0,176,202,0.03)', _dark: 'rgba(0,176,202,0.08)' }
                    : 'transparent'
                }
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: '#00B0CA' }}
                onClick={() => {
                  setSelectedColor(color);
                  setStage(1);
                }}
              >
                <Box
                  w="26px"
                  h="26px"
                  borderRadius="full"
                  bg={color.hex}
                  border="1px solid rgba(0,0,0,0.15)"
                />
                <Text
                  fontSize="12px"
                  fontWeight="600"
                  color={isSelected ? '#00B0CA' : { base: '#0A1B2B', _dark: '#E2EFF5' }}
                  textAlign="center"
                >
                  {color.value}
                </Text>
              </Flex>
            );
          })}
        </Grid>
      </Flex>
    );
  }

  /* ── Stage 1: Eye selection ── */
  if (stage === 1) {
    return (
      <Flex flexDir="column" gap="14px" w="100%">
        <Flex align="center" justify="space-between" gap="8px">
          <Flex align="center" gap="8px">
            <Flex
              w="28px"
              h="28px"
              bg="rgba(0,176,202,0.1)"
              borderRadius="8px"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon w="13px" h="13px" color="#00B0CA">
                <FaEye />
              </Icon>
            </Flex>
            <Text fontSize="14px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
              Configure as suas lentes
            </Text>
          </Flex>
          {hasColors && selectedColor && (
            <Flex
              as="button"
              align="center"
              gap="6px"
              px="10px"
              h="30px"
              borderRadius="full"
              bg="transparent"
              border="1.5px solid"
              borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.12)' }}
              cursor="pointer"
              onClick={() => setStage(0)}
            >
              <Box w="12px" h="12px" borderRadius="full" bg={selectedColor.hex} />
              <Text fontSize="12px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                {selectedColor.value}
              </Text>
            </Flex>
          )}
        </Flex>
        <Text fontSize="13px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
          Selecione os olhos que pretende configurar.
        </Text>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap="10px">
          {EYE_OPTIONS.map((opt) => {
            const isSelected = selectedEye === opt.value;
            return (
              <Flex
                key={opt.value}
                as="button"
                flexDir="column"
                align="center"
                justify="center"
                gap="8px"
                py="16px"
                px="12px"
                borderRadius="16px"
                border="2px solid"
                borderColor={
                  isSelected
                    ? '#00B0CA'
                    : { base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }
                }
                bg={
                  isSelected
                    ? { base: 'rgba(0,176,202,0.03)', _dark: 'rgba(0,176,202,0.08)' }
                    : 'transparent'
                }
                boxShadow={
                  isSelected
                    ? '0 0 0 4px rgba(0,176,202,0.12), 0 4px 20px rgba(0,176,202,0.08)'
                    : 'none'
                }
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: '#00B0CA' }}
                onClick={() => {
                  setSelectedEye(opt.value);
                  setStage(2);
                }}
              >
                <Flex gap="4px">
                  {Array.from({ length: opt.count }).map((_, i) => (
                    <Icon
                      key={i}
                      w="20px"
                      h="20px"
                      color={
                        isSelected
                          ? '#00B0CA'
                          : { base: 'rgba(0,0,0,0.28)', _dark: 'rgba(255,255,255,0.28)' }
                      }
                    >
                      <FaEye />
                    </Icon>
                  ))}
                </Flex>
                <Text
                  fontSize="15px"
                  fontWeight="700"
                  color={isSelected ? '#00B0CA' : { base: '#0A1B2B', _dark: '#E2EFF5' }}
                >
                  {opt.label}
                </Text>
                <Text
                  fontSize="11px"
                  color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}
                >
                  {opt.description}
                </Text>
              </Flex>
            );
          })}
        </Grid>
      </Flex>
    );
  }

  /* ── Stage 2: Parameter configuration ── */
  if (stage === 2 && selectedEye) {
    const eyeLabel =
      selectedEye === 'left'
        ? 'Olho Esquerdo (OE)'
        : selectedEye === 'right'
          ? 'Olho Direito (OD)'
          : 'Ambos os olhos';

    const rowStyle = {
      borderBottom: '1px solid' as const,
      borderColor: { base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.04)' },
    };

    return (
      <Flex flexDir="column" gap="14px" w="100%">
        {/* Header */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="8px">
            <Flex
              w="28px"
              h="28px"
              bg="rgba(0,176,202,0.1)"
              borderRadius="8px"
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon w="13px" h="13px" color="#00B0CA">
                <FaEye />
              </Icon>
            </Flex>
            <Text fontSize="14px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
              {eyeLabel}
            </Text>
          </Flex>
          <Flex
            as="button"
            align="center"
            gap="5px"
            px="12px"
            h="32px"
            borderRadius="full"
            bg="transparent"
            border="1.5px solid"
            borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.12)' }}
            cursor="pointer"
            transition="all 0.2s"
            _hover={{ bg: { base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.04)' } }}
            onClick={() => {
              setStage(1);
              setSelectedEye(null);
              setLeftEye(null);
              setRightEye(null);
              setIsReady(false);
              setShowConfirm(false);
              onReset?.();
            }}
          >
            <Icon w="11px" h="11px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>
              <MdOutlineArrowBackIos />
            </Icon>
            <Text fontSize="12px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
              Alterar
            </Text>
          </Flex>
        </Flex>

        {/* Column headers (both eyes only) */}
        {selectedEye === 'both' && (
          <Flex align="center">
            <Box flex="1" />
            <Flex flex="1" justify="center">
              <Flex align="center" gap="4px">
                <Icon w="11px" h="11px" color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}>
                  <FaEye />
                </Icon>
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                  color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}
                >
                  OE
                </Text>
              </Flex>
            </Flex>
            <Flex flex="1" justify="center">
              <Flex align="center" gap="4px">
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                  color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}
                >
                  OD
                </Text>
                <Icon w="11px" h="11px" color={{ base: 'rgba(0,0,0,0.38)', _dark: 'rgba(255,255,255,0.35)' }}>
                  <FaEye />
                </Icon>
              </Flex>
            </Flex>
          </Flex>
        )}

        {/* Parameter rows */}
        <Flex flexDir="column">
          {contact.base_curve.length > 0 && (
            <Flex align="center" py="10px" gap="8px" {...rowStyle}>
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Curva Base
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('left', contact.base_curve, 'base_curve')}</Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('right', contact.base_curve, 'base_curve')}</Box>
              )}
            </Flex>
          )}

          {contact.diameter.length > 0 && (
            <Flex align="center" py="10px" gap="8px" {...rowStyle}>
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Diâmetro
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('left', contact.diameter, 'diameter')}</Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('right', contact.diameter, 'diameter')}</Box>
              )}
            </Flex>
          )}

          {contact.spherical_power.length > 0 && (
            <Flex align="center" py="10px" gap="8px" {...rowStyle}>
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Potência Esférica
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">
                  <SphericalPowerSelect
                    value={leftEye?.spherical_power}
                    onChange={(v) => setLeftEye((prev) => ({ ...prev, spherical_power: v }))}
                    options={contact.spherical_power}
                  />
                </Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">
                  <SphericalPowerSelect
                    value={rightEye?.spherical_power}
                    onChange={(v) => setRightEye((prev) => ({ ...prev, spherical_power: v }))}
                    options={contact.spherical_power}
                  />
                </Box>
              )}
            </Flex>
          )}

          {contact.cylindrical_power && contact.cylindrical_power.length > 0 && (
            <Flex align="center" py="10px" gap="8px" {...rowStyle}>
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Potência Cilíndrica
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('left', contact.cylindrical_power, 'cylindrical_power')}</Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('right', contact.cylindrical_power, 'cylindrical_power')}</Box>
              )}
            </Flex>
          )}

          {contact.axle && contact.axle.length > 0 && (
            <Flex align="center" py="10px" gap="8px" {...rowStyle}>
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Eixo
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('left', contact.axle, 'axle')}</Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('right', contact.axle, 'axle')}</Box>
              )}
            </Flex>
          )}

          {contact.addition && contact.addition.length > 0 && (
            <Flex align="center" py="10px" gap="8px">
              <Box flex="1">
                <Text fontSize="13px" fontWeight="600" color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}>
                  Adição
                </Text>
              </Box>
              {(selectedEye === 'left' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('left', contact.addition, 'addition')}</Box>
              )}
              {(selectedEye === 'right' || selectedEye === 'both') && (
                <Box flex="1">{renderSelect('right', contact.addition, 'addition')}</Box>
              )}
            </Flex>
          )}
        </Flex>

        {/* Confirm button */}
        {isReady && (
          <Flex justify="flex-end" pt="4px">
            <Button
              onClick={() => setShowConfirm(true)}
              h="40px"
              px="24px"
              borderRadius="full"
              bg="#00B0CA"
              color="#FFFFFF"
              fontWeight="700"
              fontSize="13px"
              _hover={{ bg: '#0097AE' }}
            >
              Confirmar Configuração
            </Button>
          </Flex>
        )}

        {/* Confirmation Dialog */}
        <Dialog.Root open={showConfirm} onOpenChange={(e) => setShowConfirm(e.open)}>
          <Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner>
              <Dialog.Content
                bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
                borderRadius="20px"
                border="2px solid"
                borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
                boxShadow="0 8px 40px rgba(0,0,0,0.18)"
                overflow="hidden"
                maxW="480px"
                w="90vw"
                p="0"
              >
                {/* Dialog header */}
                <Flex
                  align="center"
                  gap="10px"
                  px="20px"
                  py="16px"
                  borderBottom="1px solid"
                  borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.05)' }}
                >
                  <Flex
                    w="32px"
                    h="32px"
                    bg="rgba(0,176,202,0.1)"
                    borderRadius="10px"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon w="15px" h="15px" color="#00B0CA">
                      <FaEye />
                    </Icon>
                  </Flex>
                  <Flex flexDir="column" gap="1px" flex="1">
                    <Text fontSize="15px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                      Confirmar configuração
                    </Text>
                    <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.45)', _dark: 'rgba(255,255,255,0.4)' }}>
                      Verifique se está correto antes de adicionar ao carrinho.
                    </Text>
                  </Flex>
                  <Dialog.CloseTrigger asChild>
                    <CloseButton
                      size="sm"
                      borderRadius="full"
                      color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
                    />
                  </Dialog.CloseTrigger>
                </Flex>

                {/* Dialog body */}
                <Flex flexDir="column" px="20px" py="16px">
                  {selectedColor && (
                    <Flex align="center" justify="space-between" pb="12px" mb="12px" borderBottom="1px solid" borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.05)' }}>
                      <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
                        Cor
                      </Text>
                      <Flex align="center" gap="6px">
                        <Box w="12px" h="12px" borderRadius="full" bg={selectedColor.hex} />
                        <Text fontSize="12px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                          {selectedColor.value}
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                  <Flex gap="10px" flexDir={{ base: 'column', sm: selectedEye === 'both' ? 'row' : 'column' }}>
                    {(selectedEye === 'left' || selectedEye === 'both') && (
                      <Box
                        flex="1"
                        borderRadius="12px"
                        border="1.5px solid"
                        borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.08)' }}
                        overflow="hidden"
                      >
                        <Flex
                          align="center"
                          gap="6px"
                          px="12px"
                          py="8px"
                          bg={{ base: 'rgba(0,176,202,0.05)', _dark: 'rgba(0,176,202,0.1)' }}
                          borderBottom="1px solid"
                          borderColor={{ base: 'rgba(0,176,202,0.12)', _dark: 'rgba(0,176,202,0.15)' }}
                        >
                          <Icon w="11px" h="11px" color="#00B0CA"><FaEye /></Icon>
                          <Text fontSize="11px" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color="#00B0CA">
                            OE — Olho Esquerdo
                          </Text>
                        </Flex>
                        <Flex flexDir="column" px="12px">
                          {PARAM_LABELS.filter(({ key }) =>
                            (contact[key as keyof ContactLensesProps] as { value: string }[]).length > 0
                          ).map(({ key, label }, i, arr) => (
                            <Flex
                              key={key}
                              align="center"
                              justify="space-between"
                              py="7px"
                              borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                              borderColor={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.04)' }}
                            >
                              <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
                                {label}
                              </Text>
                              <Text fontSize="12px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                                {getVal(leftEye?.[key], key)}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                    )}
                    {(selectedEye === 'right' || selectedEye === 'both') && (
                      <Box
                        flex="1"
                        borderRadius="12px"
                        border="1.5px solid"
                        borderColor={{ base: 'rgba(0,0,0,0.08)', _dark: 'rgba(255,255,255,0.08)' }}
                        overflow="hidden"
                      >
                        <Flex
                          align="center"
                          gap="6px"
                          px="12px"
                          py="8px"
                          bg={{ base: 'rgba(0,176,202,0.05)', _dark: 'rgba(0,176,202,0.1)' }}
                          borderBottom="1px solid"
                          borderColor={{ base: 'rgba(0,176,202,0.12)', _dark: 'rgba(0,176,202,0.15)' }}
                        >
                          <Text fontSize="11px" fontWeight="700" letterSpacing="0.5px" textTransform="uppercase" color="#00B0CA">
                            OD — Olho Direito
                          </Text>
                          <Icon w="11px" h="11px" color="#00B0CA" ml="auto"><FaEye /></Icon>
                        </Flex>
                        <Flex flexDir="column" px="12px">
                          {PARAM_LABELS.filter(({ key }) =>
                            (contact[key as keyof ContactLensesProps] as { value: string }[]).length > 0
                          ).map(({ key, label }, i, arr) => (
                            <Flex
                              key={key}
                              align="center"
                              justify="space-between"
                              py="7px"
                              borderBottom={i < arr.length - 1 ? '1px solid' : 'none'}
                              borderColor={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.04)' }}
                            >
                              <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.45)' }}>
                                {label}
                              </Text>
                              <Text fontSize="12px" fontWeight="700" color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}>
                                {getVal(rightEye?.[key], key)}
                              </Text>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                </Flex>

                {/* Dialog footer */}
                <Flex
                  gap="8px"
                  px="20px"
                  py="14px"
                  borderTop="1px solid"
                  borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.05)' }}
                >
                  <Button
                    onClick={() => setShowConfirm(false)}
                    flex="1"
                    h="40px"
                    borderRadius="full"
                    bg="transparent"
                    border="1.5px solid"
                    borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.12)' }}
                    fontSize="13px"
                    fontWeight="600"
                    color={{ base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' }}
                    _hover={{ bg: { base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.04)' } }}
                  >
                    Rever
                  </Button>
                  <Button
                    onClick={() => {
                      onFormComplete(
                        JSON.stringify({
                          OD: rightEye,
                          OE: leftEye,
                          ...(selectedColor ? { color: selectedColor.value } : {}),
                        }),
                      );
                      setShowConfirm(false);
                      setIsReady(false);
                    }}
                    flex="1"
                    h="40px"
                    borderRadius="full"
                    bg="#00B0CA"
                    color="#FFFFFF"
                    fontWeight="700"
                    fontSize="13px"
                    _hover={{ bg: '#0097AE' }}
                  >
                    Confirmar Graduação
                  </Button>
                </Flex>
              </Dialog.Content>
            </Dialog.Positioner>
          </Portal>
        </Dialog.Root>
      </Flex>
    );
  }

  return null;
};

export default ContactLensesOptions;
